import { APP_BASE_HREF } from '@angular/common';
import {
  Component,
  ElementRef,
  Injector,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EnhancedDialogService } from '@embraceit/coreui-shared';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { injectTranslateService } from '@embraceit/angularcore/tms';
import { Store } from '@ngxs/store';
import { DateTime, Duration } from 'luxon';
import { LocationType } from 'planning-portal-library/src/lib/core/domain/enums/location-types';
import { MasterPlanTypes } from 'planning-portal-library/src/lib/core/domain/enums/master-plan-types';
import { Tenant } from 'planning-portal-library/src/lib/core/domain/models/tenant';
import { Weekday } from 'planning-portal-library/src/lib/core/domain/models/week-days';
import { PlanDetailsRequest } from 'planning-portal-library/src/lib/core/domain/requests/plans/plan-details-request';
import { ValidateJLCsvFileResponse } from 'planning-portal-library/src/lib/core/domain/requests/plans/ValidateJLCsvFileResponse';
import { BaseRequest } from 'planning-portal-library/src/lib/data/shared/base.request';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FilterMetadata } from 'primeng/api/filtermetadata';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { firstValueFrom, lastValueFrom, Subject, Subscription } from 'rxjs';
import { nameof } from 'ts-simple-nameof';
import {
  DistributionLane,
  Job,
  JobDetails,
  LoaderHandler,
  Location,
  MasterPlan,
  Plan,
  ValidateJobTerminalsAndLocationRequest,
  ValidateNewJobTerminalsAndLocationRequest,
} from '../../../core/domain';
import { EntityTypes, LocationTypeName } from '../../../core/domain/enums';
import { PlanJobs } from '../../../core/domain/models/plan-details';
import {
  CalculateJobETAUsecase,
  GetDistributionLanesUsecase,
  GetFeatureConfigurationUsecase,
  GetGridHeadersUsecase,
  GetJobTemplateUsecase,
  GetLocationCountByProductUseCase,
  GetLocationCountUseCase,
  GetMapProviderUsecase,
  GetPlanByIdUsecase,
  GetPlanContainerByIdUseCase,
  GetPlanJobDetailsUsecase,
  GetProductsUsecase,
  GetTenantByIdUsecase,
  GetVehicleTypesUsecase,
  ValidateJobTerminalsAndLocationUsecase,
  ValidateNewJobTerminalsAndLocationUsecase,
} from '../../../core/usecases';
import { GetJobTemplate, GetVehicleTypes } from '../../jobs/state/jobs.actions';
import {
  GetLocationsCount,
  GetLocationsCountByProduct,
  GetPlanJobDetails,
  IsAddEditJobShow,
  IsEditMode,
  SetMasterPlanId,
} from '../../mater-plans/new-edit-master-plan/state/new-edit-master-plan.action';
import { NewEditMasterPlansState } from '../../mater-plans/new-edit-master-plan/state/new-edit-master-plan.state';
import {
  GetProducts,
  SelectedDistributionLaneId,
  SelectedMasterPlan,
  SelectedPlan,
  SetPlanId,
} from '../../mater-plans/state/master-plan.actions';
import {
  SetTenant,
  ValidateJobTerminals,
} from '../../state/planning-portal-global-state/planning-portal-global.actions';
import { PlanningPortalGlobalState } from '../../state/planning-portal-global-state/planning-portal-global.state';
import { ConfigurationsComponent } from '../configurations/configurations.component';
import {
  DisableLoading,
  EnableLoading,
  GetGridHeaders,
  SetBaseRoute,
  SetFeatureConfigurations,
  SetMapProviders,
  SetSelectedJobDetailForBreadcrumb,
  SetSelectedJobForBreadcrumb,
  SetSelectedLocationForBreadcrumb,
  SetSelectedMasterPlanForBreadcrumb,
  SetSelectedPlanDetailForBreadcrumb,
  SetSelectedPlanForBreadcrumb,
  SetValidatedJLDataForPlan,
} from '../state/base.actions';
import { BaseState } from '../state/base.state';
import { Menu } from 'primeng/menu';

@Component({
  template: '',
  standalone: false,
})
export abstract class PlanningPortalBaseComponent {
  public subscriptions: Subscription[] = [];
  public destroyer$: Subject<boolean> = new Subject<boolean>();
  public store: Store;
  public getLocationsCountUsesase: GetLocationCountUseCase;
  public getMasterPlanByIdUsecase: GetPlanContainerByIdUseCase;
  public getJobTemplateUsecase: GetJobTemplateUsecase;
  public getLocationsCountByProductUsesase: GetLocationCountByProductUseCase;
  public translate = injectTranslateService();
  public dialogService: EnhancedDialogService;
  public entityType = EntityTypes;
  public getGridHeadersUsecase: GetGridHeadersUsecase;
  public confirmationService: ConfirmationService;
  public messageService: MessageService;
  public lazyLoadEvent!: TableLazyLoadEvent;
  public getDistributionLanesUsecase: GetDistributionLanesUsecase;
  public getAllPlanJobsUsecase: GetPlanJobDetailsUsecase;
  public getTenantByIdUsecase: GetTenantByIdUsecase;
  public getFeatureConfigurationUsecase: GetFeatureConfigurationUsecase;
  public getProductsUseCase: GetProductsUsecase;
  public validateJobTerminalsAndLocationUsecase: ValidateJobTerminalsAndLocationUsecase;
  public validateNewJobTerminalsAndLocationUsecase: ValidateNewJobTerminalsAndLocationUsecase;
  private calculateJobETAUsecase: CalculateJobETAUsecase;
  private getVehicleTypesUsecase: GetVehicleTypesUsecase;
  private getPlanByIdUsecase: GetPlanByIdUsecase;
  public getMapProviderUseCase: GetMapProviderUsecase;
  public router: Router;
  public baseHref: string;
  public activatedRoute: ActivatedRoute;
  private elref: ElementRef;
  private renderer2: Renderer2;
  protected injector: Injector;

  loaderHandler = false;
  distributionLanes: DistributionLane[] | undefined;
  isScrolling = false;
  @ViewChildren('dropdownRef') dropdownRef!: QueryList<NgbDropdown>;
  tenant: Tenant | undefined;
  isFileUploadErr = false;
  selectedMasterPlanTypeId: number | undefined;

  protected constructor(injector: Injector) {
    this.injector = injector;
    this.store = injector.get(Store);
    this.getMasterPlanByIdUsecase = injector.get(GetPlanContainerByIdUseCase);
    this.getProductsUseCase = injector.get(GetProductsUsecase);
    this.getTenantByIdUsecase = injector.get(GetTenantByIdUsecase);
    this.getFeatureConfigurationUsecase = injector.get(
      GetFeatureConfigurationUsecase
    );
    this.getLocationsCountUsesase = injector.get(GetLocationCountUseCase);
    this.getLocationsCountByProductUsesase = injector.get(
      GetLocationCountByProductUseCase
    );
    this.getPlanByIdUsecase = injector.get(GetPlanByIdUsecase);
    this.calculateJobETAUsecase = injector.get(CalculateJobETAUsecase);
    this.dialogService = injector.get(EnhancedDialogService);
    this.getGridHeadersUsecase = injector.get(GetGridHeadersUsecase);
    this.confirmationService = injector.get(ConfirmationService);
    this.messageService = injector.get(MessageService);
    this.getDistributionLanesUsecase = injector.get(
      GetDistributionLanesUsecase
    );
    this.getMapProviderUseCase = injector.get(GetMapProviderUsecase);
    this.getJobTemplateUsecase = injector.get(GetJobTemplateUsecase);
    this.getAllPlanJobsUsecase = injector.get(GetPlanJobDetailsUsecase);
    this.validateJobTerminalsAndLocationUsecase = injector.get(
      ValidateJobTerminalsAndLocationUsecase
    );
    this.validateNewJobTerminalsAndLocationUsecase = injector.get(
      ValidateNewJobTerminalsAndLocationUsecase
    );
    this.getVehicleTypesUsecase = injector.get(GetVehicleTypesUsecase);
    this.router = injector.get(Router);
    this.activatedRoute = injector.get(ActivatedRoute);
    this.baseHref = injector.get(APP_BASE_HREF);
    const loadingSub = this.store
      .select(BaseState.Loading)
      .subscribe((loaderHandler: LoaderHandler[] | undefined) => {
        if (loaderHandler && loaderHandler?.length > 0) {
          this.loaderHandler = true;
        } else {
          this.loaderHandler = false;
        }
      });
    this.subscriptions.push(loadingSub!);
    const TenantSub = this.store
      .select(PlanningPortalGlobalState.Tenant)
      .subscribe((value) => {
        if (value) {
          this.tenant = value;
        }
      });
    this.subscriptions.push(TenantSub);
    this.elref = injector.get(ElementRef);
    this.renderer2 = injector.get(Renderer2);
  }

  sortWeekdays(weekDays: Weekday[]): Weekday[] {
    const monday = weekDays.find((wd) => wd.id == 1);
    const sunday = weekDays.find((wd) => wd.id == 0);
    weekDays = weekDays.filter((wd) => wd.id !== 1 && wd.id !== 0);

    const weekDaysSorted = weekDays.sort((a, b) => a.id - b.id);

    const sortedWeekDays = [monday!, ...weekDaysSorted, sunday!];

    return sortedWeekDays;
  }

  sortPlandays(planDays: number[]): number[] {
    let sortedWeekDays: number[] = [];

    if (planDays) {
      const monday = planDays.find((pd) => pd == 1);
      const sunday = planDays.find((pd) => pd == 0);
      planDays = planDays.filter((pd) => pd !== 1 && pd !== 0);

      const weekDaysSorted = planDays.sort((a, b) => a - b);

      if (!monday && sunday !== null && sunday !== undefined) {
        //Sunday can not be put as !sunday in the condition because it can take 0 value

        sortedWeekDays = [...weekDaysSorted, sunday!];
      } else if (monday && (sunday == null || sunday == undefined)) {
        sortedWeekDays = [monday, ...weekDaysSorted];
      } else if (!monday && !sunday) {
        sortedWeekDays = [...weekDaysSorted];
      } else {
        sortedWeekDays = [monday!, ...weekDaysSorted, sunday!];
      }
    }

    return sortedWeekDays;
  }

  async getPlanById(request: string) {
    if (request) {
      const loaderHandler: LoaderHandler = {
        componentName: PlanningPortalBaseComponent.name,
        endPointName: nameof<PlanningPortalBaseComponent>(
          (nep) => nep.getPlanById
        ),
      };
      this.enableLoading(loaderHandler);
      try {
        const planById$ = this.getPlanByIdUsecase.execute(request);
        const planById = await lastValueFrom(planById$, {
          defaultValue: undefined,
        });
        if (planById) {
          this.store.dispatch(new SelectedPlan(planById));
          this.store.dispatch(new SetPlanId(planById.id));
        }
      } catch (e: any) {
        let message =
          'master_plans.common_messages.errors.something_went_wrong_try_again';
        if (e?.error) {
          message = e.error.message ? e.error.message : e.error.errors[0];
        }
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.errors.error'),
          detail: this.translate.instant(message),
          life: 3000,
        });
      } finally {
        this.disableLoading(loaderHandler);
      }
    }
  }

  async getMapProviders() {
    const mapProvider$ = this.getMapProviderUseCase.execute();
    const mapProvider = await lastValueFrom(mapProvider$, {
      defaultValue: undefined,
    });
    if (mapProvider) {
      setTimeout(() => {
        const tenant = this.store.selectSnapshot(
          PlanningPortalGlobalState.Tenant
        );
        mapProvider?.forEach((provider) => {
          if (provider.id.toLowerCase() == tenant?.viewMapId.toLowerCase()) {
            provider.isSelected = true;
          }
        });
        this.store.dispatch(new SetMapProviders(mapProvider));
      }, 1000);
    }
  }

  async getVehicleTypes() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.getVehicleTypes
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const getVehicleTypes$ = this.getVehicleTypesUsecase.execute();
      const getVehicleTypes = await lastValueFrom(getVehicleTypes$, {
        defaultValue: undefined,
      });
      this.store.dispatch(new GetVehicleTypes(getVehicleTypes));
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  
  async calculateJobETA(jobIds: string[]) {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.calculateJobETA
      ),
    };

    const connectionId = this.store.selectSnapshot(
      PlanningPortalGlobalState.ConnectionId
    );

    this.enableLoading(loaderHandler);

    try {
      const calculateJobETA$ = this.calculateJobETAUsecase.execute({
        JobIds: jobIds,
        ConnectionId: connectionId ?? '',
      });

      const calculateJobETA = await lastValueFrom(calculateJobETA$, {
        defaultValue: undefined,
      });

      if (calculateJobETA) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success.success'),
          detail: this.translate.instant(
            'master_plans.common_messages.messages.message_successfully_sent_for_processing'
          ),
          life: 3000,
        });
      } else if (calculateJobETA === false) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.errors.error'),
          detail: this.translate.instant(
            'master_plans.jobs.messages.failed_to_calculate_job_eta'
          ),
          life: 3000,
        });
      }
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }

    return false;
  }

  async validateJobTerminalsAndLocation(
    request: ValidateJobTerminalsAndLocationRequest
  ): Promise<boolean> {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.validateJobTerminalsAndLocationUsecase
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const validateJobTerminals$ =
        this.validateJobTerminalsAndLocationUsecase.execute(request);
      const validateJobTerminals = await lastValueFrom(validateJobTerminals$, {
        defaultValue: undefined,
      });

      this.store.dispatch(new ValidateJobTerminals(validateJobTerminals));
      return validateJobTerminals!;
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
      return false;
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  async validateNewJobTerminalsAndLocation(
    request: ValidateNewJobTerminalsAndLocationRequest
  ): Promise<boolean> {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.validateJobTerminalsAndLocationUsecase
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const validateJobTerminals$ =
        this.validateNewJobTerminalsAndLocationUsecase.execute(request);
      const validateJobTerminals = await lastValueFrom(validateJobTerminals$, {
        defaultValue: undefined,
      });
      this.store.dispatch(new ValidateJobTerminals(validateJobTerminals));
      return validateJobTerminals!;
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
      return false;
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  async getMasterPlanById(request: string) {
    if (request != null) {
      const loaderHandler: LoaderHandler = {
        componentName: PlanningPortalBaseComponent.name,
        endPointName: nameof<PlanningPortalBaseComponent>(
          (nep) => nep.getMasterPlanById
        ),
      };
      this.enableLoading(loaderHandler);
      try {
        const masterPlanById$ = this.getMasterPlanByIdUsecase.execute(request);
        const masterPlanById = await lastValueFrom(masterPlanById$, {
          defaultValue: undefined,
        });
        if (masterPlanById) {
          this.store.dispatch(
            new SelectedDistributionLaneId(masterPlanById.distributionLaneId)
          );
          this.store.dispatch(new SelectedMasterPlan(masterPlanById));
        }
      } catch (e: any) {
        let message =
          'master_plans.common_messages.errors.something_went_wrong_try_again';
        if (e?.error) {
          message = e.error.message ? e.error.message : e.error.errors[0];
        }
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.errors.error'),
          detail: this.translate.instant(message),
          life: 3000,
        });
      } finally {
        this.disableLoading(loaderHandler);
      }
    }
  }
  async getJobTemplate() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.getJobTemplate
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const getJobTemplate$ = this.getJobTemplateUsecase.execute();
      const getJobTemplate = await lastValueFrom(getJobTemplate$, {
        defaultValue: undefined,
      });
      if (getJobTemplate) {
        this.store.dispatch(new GetJobTemplate(getJobTemplate));
      }
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  isNumber(value: any): boolean {
    return typeof value === 'number' && !isNaN(value);
  }

  async getTenantById() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.getTenantById
      ),
    };
    this.enableLoading(loaderHandler);

    try {
      const tenantId = this.store.selectSnapshot(
        PlanningPortalGlobalState.TenantId
      );

      if (tenantId) {
        const tenant$ = this.getTenantByIdUsecase.execute(tenantId);
        const tenant = await lastValueFrom(tenant$, {
          defaultValue: undefined,
        });
        this.store.dispatch(new SetTenant(tenant));
      }
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message =
          e.error.message ??
          (Array.isArray(e.error.errors) && e.error.errors.length > 0
            ? e.error.errors[0]
            : message || this.translate);
      }
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  async getFeatureConfiguration() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.getFeatureConfiguration
      ),
    };
    this.enableLoading(loaderHandler);

    try {
      const featureConfiguration$ =
        this.getFeatureConfigurationUsecase.execute();
      const featureConfiguration = await lastValueFrom(featureConfiguration$, {
        defaultValue: undefined,
      });
      this.store.dispatch(new SetFeatureConfigurations(featureConfiguration));
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message =
          e.error.message ??
          (Array.isArray(e.error.errors) && e.error.errors.length > 0
            ? e.error.errors[0]
            : message || this.translate);
      }
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  async isFeatureEnabled(name: string): Promise<boolean> {
    let featureConfiguration = this.store.selectSnapshot(
      BaseState.featureConfigurations
    );
    if (featureConfiguration && featureConfiguration.length > 0) {
      return (
        featureConfiguration.find((a) => a.name === name)?.isEnabled ?? false
      );
    } else {
      await this.getFeatureConfiguration();
      featureConfiguration = this.store.selectSnapshot(
        BaseState.featureConfigurations
      );
      return featureConfiguration && featureConfiguration.length > 0
        ? featureConfiguration.find((a) => a.name === name)?.isEnabled ?? false
        : false;
    }
  }
  async getTenantConfigurationByKey(key: string): Promise<string | null> {
    let featureConfiguration = this.store.selectSnapshot(BaseState.featureConfigurations);

    if (featureConfiguration?.length === 0) {
      return null;
    }

    const feature = featureConfiguration?.find((a) => a.name === key);

    return feature?.value ?? null;
  }

  async selectProducts() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (nep) => nep.selectProducts
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const products$ = this.getProductsUseCase.execute();
      const products = await lastValueFrom(products$, {
        defaultValue: undefined,
      });
      this.store.dispatch(new GetProducts(products));
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  async getPlansDetails(param: string) {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.getPlansDetails
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const planDetailsRequest: BaseRequest<PlanDetailsRequest> = {
        request: {
          id: param,
        },
      };
      const planDetails$ =
        this.getAllPlanJobsUsecase.execute(planDetailsRequest);
      const planDetails = await lastValueFrom(planDetails$, {
        defaultValue: undefined,
      });

      if (planDetails) {
        this.store?.dispatch(new GetPlanJobDetails(planDetails));
      }
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.redirectToNewEditMasterPlan(
        this.store.selectSnapshot(NewEditMasterPlansState.masterPlanId)!
      );
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  openConfigurations(entityType: number): DynamicDialogRef {
    return this.dialogService.open(ConfigurationsComponent, {
      styleClass: 'p-dialog-xl',
      closable: true,
      modal: true,
    });
  }

  isDateTime(value: unknown): string | undefined {
    if (value) {
      if (DateTime.isDateTime(value)) {
        return 'DateTime';
      } else if (Duration.isDuration(value)) {
        return 'Duration';
      } else {
        return undefined;
      }
    }
    return '';
  }
  getLocationType(type: LocationType): string {
    switch (type) {
      case LocationType.MasterLocation:
        return LocationTypeName.MasterLocation;
      case LocationType.ImportFromCSV:
        return LocationTypeName.ImportFromCSV;
      case LocationType.ManualEntry:
        return LocationTypeName.ManualEntry;
      default:
        return '';
    }
  }

  async getLocationsCountByLocationType(masterPlanId?: string) {
    let planContainerId = '';
    if (masterPlanId) {
      planContainerId = masterPlanId;
    } else {
      planContainerId = this.store.selectSnapshot(
        NewEditMasterPlansState.masterPlanId
      )!;
    }
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.getLocationsCountByLocationType
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const getLocationsCount$ = this.getLocationsCountUsesase.execute(
        planContainerId!
      );
      const getLocationsCount = await lastValueFrom(getLocationsCount$, {
        defaultValue: undefined,
      });
      this.store.dispatch(new GetLocationsCount(getLocationsCount));
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  async getLocationCountByProduct(masterPlanId?: string) {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.getLocationsCountByLocationType
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const getLocationsCount$ = this.getLocationsCountByProductUsesase.execute(
        masterPlanId!
      );
      const getLocationsCount = await lastValueFrom(getLocationsCount$, {
        defaultValue: undefined,
      });

      this.store.dispatch(new GetLocationsCountByProduct(getLocationsCount));
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }
  getFilterData(
    event: TableLazyLoadEvent,
    key: string
  ): any | null | undefined {
    return event.filters?.[key] &&
      (event.filters?.[key] as FilterMetadata)?.value
      ? (event.filters?.[key] as FilterMetadata)?.value
      : null;
  }

  async getDistributionLanes() {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>(
        (pc) => pc.getDistributionLanes
      ),
    };
    this.enableLoading(loaderHandler);
    try {
      const baseRequest: BaseRequest<string> = {
        request: '',
      };
      const distributionLanes$ =
        this.getDistributionLanesUsecase?.execute(baseRequest);
      this.distributionLanes = await lastValueFrom(distributionLanes$, {
        defaultValue: undefined,
      });
    } catch (e) {
      console.error(e);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(
          'common.messages.errors.something_went_wrong_try_again'
        ),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  async fetchHeader(entityType: number) {
    const loaderHandler: LoaderHandler = {
      componentName: PlanningPortalBaseComponent.name,
      endPointName: nameof<PlanningPortalBaseComponent>((bc) => bc.fetchHeader),
    };
    this.enableLoading(loaderHandler);
    try {
      const gridHeaders$ = await this.getGridHeadersUsecase.execute({
        request: {
          entityType: entityType,
        },
      });
      const gridHeaders = await firstValueFrom(gridHeaders$);
      this.store.dispatch(new GetGridHeaders(gridHeaders));
    } catch (e) {
      console.error(e);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translate.instant(
          'common.messages.errors.something_went_wrong_try_again'
        ),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }
  }

  onOpenConfigurations(entityType: number) {
    const dialogRef = this.openConfigurations(entityType);
    const dialogRefOnCloseSub = dialogRef.onClose.subscribe(
      (response: boolean) => {
        if (response) {
          this.fetchHeader(entityType).then();
        }
      }
    );
    this.subscriptions.push(dialogRefOnCloseSub);
  }

  closeDialog() {
    this.dialogService.dialogComponentRefMap.forEach((dialog) => {
      dialog.destroy();
    });
  }

  onScroll(event: any) {
    this.dropdownRef.forEach((dropdown) => {
      if (dropdown.isOpen()) {
        dropdown.close();
      }
    });
    const scrollPosition = event.target.scrollTop;
    if (scrollPosition > 0) {
      this.isScrolling = true;
    } else {
      this.isScrolling = false;
    }
  }
  enableLoading(payload: LoaderHandler) {
    this.store.dispatch(new EnableLoading(payload));
  }

  disableLoading(payload: LoaderHandler) {
    this.store.dispatch(new DisableLoading(payload));
  }

  moveToPlanDetails(plan: Plan, baseRouteName: string) {
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['plan-details', plan.id], { relativeTo: this.activatedRoute })
      .then();
  }
  moveToNewUiUpdatePlanDetails(
    masterPlan: MasterPlan,
    plan: Plan,
    baseRouteName: string
  ) {
    this.store.dispatch(new IsEditMode(true));
    this.store.dispatch(new SetMasterPlanId(masterPlan.id));
    this.store.dispatch(new SelectedMasterPlan(masterPlan));
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.store.dispatch(new SelectedPlan(plan));
    this.store.dispatch(new SetPlanId(plan.id));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    if (masterPlan!.type == MasterPlanTypes.HDPlanning) {
      this.router
        .navigate([
          this.baseHref,
          `${baseRouteName}`,
          'edit-master-plan',
          masterPlan!.id,
          'plan-details',
          plan?.id,
        ])
        .then();
    } else {
      if (plan!.id) {
        this.router
          .navigate(
            [
              this.baseHref,
              `${baseRouteName}`,
              'edit-master-plan',
              masterPlan!.id,
              'new-update-plan-details',
              plan?.id,
            ]
            // { relativeTo: this.activatedRoute }
          )
          .then();
      } else {
        this.router
          .navigate(
            [
              this.baseHref,
              `${baseRouteName}`,
              'edit-master-plan',
              masterPlan!.id,
              'new-update-plan-details',
            ]
            // { relativeTo: this.activatedRoute }
          )
          .then();
      }
    }
  }
  moveToNewUiUpdatePlanRoutes(
    masterPlan: MasterPlan,
    plan: Plan,
    baseRouteName: string
  ) {
    this.store.dispatch(new IsEditMode(true));
    this.store.dispatch(new SetMasterPlanId(masterPlan.id));
    this.store.dispatch(new SelectedMasterPlan(masterPlan));
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.store.dispatch(new IsAddEditJobShow(false));
    this.store.dispatch(new SetPlanId(plan.id));
    this.store.dispatch(new SelectedPlan(plan));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));

    if (masterPlan.type == MasterPlanTypes.HDPlanning) {
      this.router
        .navigate(
          [
            this.baseHref,
            `${baseRouteName}`,
            'edit-master-plan',
            masterPlan!.id,
            'routes',
            plan.id,
          ],
          { relativeTo: this.activatedRoute }
        )
        .then();
    } else {
      this.router
        .navigate(
          [
            this.baseHref,
            `${baseRouteName}`,
            'edit-master-plan',
            masterPlan!.id,
            'new-update-plan-routes',
            plan.id,
          ],
          { relativeTo: this.activatedRoute }
        )
        .then();
    }
  }
  moveToNewUiUpdatePlanRouteDetails(
    masterPlan: MasterPlan,
    plan: Plan,
    baseRouteName: string
  ) {
    this.store.dispatch(new IsEditMode(true));
    this.store.dispatch(new SetMasterPlanId(masterPlan.id));
    this.store.dispatch(new SelectedMasterPlan(masterPlan));
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.store.dispatch(new SelectedPlan(plan));
    this.store.dispatch(new SetPlanId(plan.id));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(
        [
          this.baseHref,
          `${baseRouteName}`,
          'edit-master-plan',
          masterPlan!.id,
          'new-update-plan-routes',
          plan?.id,
        ],
        { relativeTo: this.activatedRoute }
      )
      .then();
  }
  moveToNewUiPlanUnassignedLocations(
    masterPlan: MasterPlan,
    plan: Plan,
    baseRouteName: string
  ) {
    this.store.dispatch(new IsEditMode(true));
    this.store.dispatch(new SetMasterPlanId(masterPlan.id));
    this.store.dispatch(new SelectedMasterPlan(masterPlan));
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.store.dispatch(new SelectedPlan(plan));
    this.store.dispatch(new SetPlanId(plan.id));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(
        [
          this.baseHref,
          `${baseRouteName}`,
          'edit-master-plan',
          masterPlan!.id,
          'new-update-plan-routes',
          plan?.id,
        ],
        { relativeTo: this.activatedRoute }
      )
      .then();
  }
  moveToJobs(plan: Plan, baseRouteName: string) {
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate([this.baseHref, `${baseRouteName}`, 'jobs', plan.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToLocations(plan: Plan, baseRouteName: string) {
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate([this.baseHref, 'locations', plan.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToLocationsFromJobs(job: Job, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedJobForBreadcrumb(job));
    this.router
      .navigate([this.baseHref, 'locations', job.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToNewMasterPlan(masterPlan?: MasterPlan) {
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.router
      .navigate(['new-master-plan'], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToNewMasterPlanOnDeletePlan(masterPlan?: MasterPlan) {
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.router
      .navigate(['new-master-plan'], { relativeTo: this.activatedRoute.parent })
      .then();
  }

  moveToNewMasterPlanOnClose() {
    this.router.navigate(['../'], { relativeTo: this.activatedRoute }).then();
  }
  moveToNewPlanonclose() {
    this.router
      .navigate(['../../'], { relativeTo: this.activatedRoute })
      .then();
  }
  moveToNewEditMasterPlan(masterPlan?: MasterPlan) {
    if (!masterPlan || !masterPlan.id) return;

    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlan.id,
      ])
      .then();
  }

  moveToNewEditMasterPlanLocationDetails(
    masterPlan?: MasterPlan,
    baseRouteName?: string,
    useRelative?: boolean | null
  ) {
    if (!masterPlan || !masterPlan.id) return;

    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.store.dispatch(new IsEditMode(true));
    this.store.dispatch(new SetMasterPlanId(masterPlan.id));
    this.store.dispatch(new SelectedMasterPlan(masterPlan));

    setTimeout(() => {
      this.scrollToElementById('importLocationDetails');
    }, 500);

    if (useRelative) {
      return this.router.navigate(
        [
          'edit-master-plan',
          masterPlan.id,
          'add-locations',
          'import-locations-details',
        ],
        { relativeTo: this.activatedRoute }
      );
    } else {
      const routeSegments = baseRouteName
        ? [
            '/',
            baseRouteName,
            'edit-master-plan',
            masterPlan.id,
            'add-locations',
            'import-locations-details',
          ]
        : [
            '/master-plans',
            'edit-master-plan',
            masterPlan.id,
            'add-locations',
            'import-locations-details',
          ];

      return this.router.navigate(routeSegments);
    }
  }

  redirectToNewEditMasterPlan(masterPlanId: string) {
    if (this.store.selectSnapshot(NewEditMasterPlansState.IsEdit)) {
      this.router
        .navigate([
          this.baseHref,
          'master-plans',
          'edit-master-plan',
          masterPlanId,
        ])
        .then();
    } else {
      this.router
        .navigate([this.baseHref, 'master-plans', 'new-master-plan'])
        .then();
    }
  }

  moveToAddLocation(masterPlan?: MasterPlan) {
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.router
      .navigate(['add-locations'], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToNewUpdatePlanDetails(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    if (plan!.id) {
      this.router
        .navigate(['new-update-plan-details', plan?.id], {
          relativeTo: this.activatedRoute,
        })
        .then();
    } else {
      this.router
        .navigate(['new-update-plan-details'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }

  moveToNewUpdateLocationGroups(plan?: Plan) {
    if (plan!.id) {
      this.router
        .navigate(['new-update-location-groups', plan?.id], {
          relativeTo: this.activatedRoute,
        })
        .then();
    } else {
      this.router
        .navigate(['new-update-location-groups'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }
  moveToHDRulesFromLocationGroup(plan?: Plan) {
    if (plan!.id) {
      this.router
        .navigate(['../../hd_rules', plan?.id], {
          relativeTo: this.activatedRoute,
        })
        .then();
    } else {
      this.router
        .navigate(['../../hd_rules'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }
  moveToHDRules(plan?: Plan) {
    if (plan!.id) {
      this.router
        .navigate(['hd_rules', plan?.id], {
          relativeTo: this.activatedRoute,
        })
        .then();
    } else {
      this.router
        .navigate(['hd_rules'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }
  moveBackToLocationGroups(plan?: Plan) {
    if (plan!.id) {
      this.router.navigate(['../../new-update-location-groups', plan?.id], {
        relativeTo: this.activatedRoute,
      });
    } else {
      this.router
        .navigate(['../new-update-location-groups'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }
  moveBackToLocationGroupAMS() {
    this.router
      .navigate(['../new-update-location-groups'], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveBackToPlanDetailsFromLocationGroupHD(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./plan-details', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveBackToPlanDetailsFromResources(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-update-plan-details', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToNewPlanResources(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    if (plan!.id) {
      this.router
        .navigate(['new-plan-resources', plan?.id], {
          relativeTo: this.activatedRoute,
        })
        .then();
    } else {
      this.router
        .navigate(['new-plan-resources'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }

  moveBackToPlanResourcesFromRules(plan?: Plan) {
    setTimeout(() => {
      this.scrollToElementById('importFromVehiclePortal');
    }, 1000);
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-plan-resources', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToNewPlanResourcesFromPlanDetails(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-plan-resources', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToImportFromVehiclePortalFromPlanDetails(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-plan-resources', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }
  moveToLocationGroupFromPlanDetails(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-update-location-groups', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToPlanResourcesFromPlanDetailsInEdit(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-plan-resources', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToImportFromVehiclePortalFromPlanDetailsInEdit(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-plan-resources', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToNewUpdatePlanRules(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['new-update-plan-rules', plan?.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToNewUpdatePlanRulesFromResources(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['new-update-plan-rules', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then(() => {
        this.scrollToElementById('rules');
      });
  }

  moveToImportVehicleOptions(planId?: string) {
    this.router
      .navigate(['../new-plan-resources', planId], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }
  moveToJobImport(data?: ValidateJLCsvFileResponse) {
    var masterPlanId = this.store.selectSnapshot(
      NewEditMasterPlansState.masterPlanId
    )!;
    this.store.dispatch(new SetValidatedJLDataForPlan(data));

    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlanId,
        'job-import',
      ])
      .then();

    setTimeout(() => {
      this.scrollToElementById('jobImport');
    }, 50);
  }
  moveToJobImportFromPlan(
    data?: ValidateJLCsvFileResponse,
    planContainerId?: string
  ) {
    this.store.dispatch(new SetValidatedJLDataForPlan(data));
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        planContainerId,
        'job-import',
      ])
      .then();
    setTimeout(() => {
      this.scrollToElementById('jobImport');
    }, 50);
  }
  moveToNewUpdatePlanRoutesFromRules(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['./new-update-plan-routes', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToPlanJobsHD(plan?: Plan) {
    var masterPlanId = this.store.selectSnapshot(
      NewEditMasterPlansState.masterPlanId
    )!;
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlanId,
        'routes',
        plan?.id,
      ])
      .then();
  }

  moveToMasterPlans() {
    this.router.navigate([this.baseHref, 'master-plans']).then();
  }

  moveToNewUpdatePlanRoutes(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['new-update-plan-routes', plan?.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToPlanRoutesFromJobDetails(plan?: Plan) {
    this.store.dispatch(new SetSelectedPlanForBreadcrumb(plan));
    this.router
      .navigate(['../', plan?.id], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToNewPlanJobDetail(jobId?: string) {
    this.router
      .navigate(['job-details', jobId], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToImportTab() {
    this.router
      .navigate(['import-locations'], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToImportTabOnBack() {
    this.router
      .navigate(['../import-locations'], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToImportTabFromContextMenu() {
    this.router
      .navigate(['add-locations/import-locations'], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToAddEditLocationGroups(locationGroupId?: string) {
    if (locationGroupId) {
      this.router
        .navigate(['add-edit-location-group', locationGroupId], {
          relativeTo: this.activatedRoute.parent,
        })
        .then();
    } else {
      this.router
        .navigate(['add-edit-location-group'], {
          relativeTo: this.activatedRoute.parent,
        })
        .then();
    }
  }

  moveToAddEditLocationGroupsFromNewMasterPlan(locationGroupId?: string) {
    setTimeout(() => {
      this.scrollToElementById('addLocations');
    }, 250);
    if (locationGroupId) {
      this.router
        .navigate(
          ['add-locations', 'add-edit-location-group', locationGroupId],
          {
            relativeTo: this.activatedRoute,
          }
        )
        .then();
    } else {
      this.router
        .navigate(['add-locations', 'add-edit-location-group'], {
          relativeTo: this.activatedRoute,
        })
        .then();
    }
  }
  moveToImportOptions(masterPlan?: MasterPlan) {
    this.router
      .navigate(['import-locations-options'], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToImportOptionsFromNewMasterPlan(masterPlan?: MasterPlan) {
    setTimeout(() => {
      this.scrollToElementById('addLocations');
    }, 250);
    this.router
      .navigate(['add-locations', 'import-locations-options'], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToImportDetailsFromNewMasterPlan(masterPlan?: MasterPlan) {
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'new-master-plan',
        'add-locations',
        'import-locations-details',
      ])
      .then();
  }

  moveToImportDetailsFromEditMasterPlan(masterPlan?: MasterPlan) {
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlan?.id,
        'add-locations',
        'import-locations-details',
      ])
      .then();
  }
  moveToHdLocations(masterPlanId?: string) {
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlanId,
        'locations',
        'import-locations-details',
      ])
      .then();
    //this.router.navigate(['../import-locations-details'], { relativeTo: this.activatedRoute.parent });
  }
  moveToImportDetails(masterPlan?: MasterPlan) {
    this.router
      .navigate(['import-locations-details'], {
        relativeTo: this.activatedRoute.parent,
      })
      .then();
  }

  moveToHDImportOptions() {
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        'locations',
        'import-options',
      ])
      .then();
  }

  moveToHDImportOptionsEdit(masterPlanId: string) {
    this.router
      .navigate([
        this.baseHref,
        'master-plans',
        'edit-master-plan',
        masterPlanId,
        'locations',
        'import-options',
      ])
      .then(() => {
        this.scrollToElementById('addLocations');
      });
  }

  moveToLocationsFromMasterPlan(masterPlan: MasterPlan, baseRouteName: string) {
    this.store.dispatch(new SetBaseRoute(baseRouteName));
    this.store.dispatch(new SetSelectedMasterPlanForBreadcrumb(masterPlan));
    this.router
      .navigate([this.baseHref, `${baseRouteName}`, 'location', masterPlan.id])
      .then();
  }

  moveToServicesFromLocation(location: Location, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedLocationForBreadcrumb(location));
    this.router
      .navigate(['services', location.id], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToServicesFromJob(jobDetail: JobDetails, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedJobDetailForBreadcrumb(jobDetail));
    this.router
      .navigate(['service', jobDetail.jobId], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToJobDetails(planJob: PlanJobs, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedPlanDetailForBreadcrumb(planJob));
    this.router
      .navigate(['job-details', planJob.jobId], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToJobDetailsFromJob(job: Job, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedJobForBreadcrumb(job));
    this.router
      .navigate(['job-details', job.id], { relativeTo: this.activatedRoute })
      .then();
  }

  moveToLocationFromJobDetails(jobDetails: JobDetails, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedJobDetailForBreadcrumb(jobDetails));
    this.router
      .navigate(['locations', jobDetails.jobId], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }

  moveToLocationFromjoblocation(location: Location, baseRouteName: string) {
    this.store.dispatch(
      new SetBaseRoute(this.store.selectSnapshot(BaseState.BaseRoute))
    );
    this.store.dispatch(new SetSelectedLocationForBreadcrumb(location));
    this.router
      .navigate(['location', location.id], {
        relativeTo: this.activatedRoute,
      })
      .then();
  }
  scrollToElementById(id: string) {
    const element = this.getElementById(id);
    this.scrollToElement(element);
  }
  getElementById(id: string): HTMLElement {
    const element: any = document.getElementById(id);
    return element;
  }
  scrollToElement(element: HTMLElement) {
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  }

  async loadDropdown(
    componentName: string,
    endPointName: string,
    state: any,
    useCase: any,
    classType: any,
    storeData = true,
    params: any = undefined
  ) {
    let result: any;

    const loaderHandler: LoaderHandler = {
      componentName: componentName,
      endPointName: endPointName,
    };

    this.enableLoading(loaderHandler);

    try {
      let data;

      if (!data && storeData) {
        const dataAux = params ? useCase.execute(params) : useCase.execute();
        data = await firstValueFrom(dataAux, { defaultValue: [] });
        const newInstance = this.createInstance<typeof classType>(
          classType,
          data
        );
        this.store.dispatch(<typeof classType>newInstance);
      } else if (!data) {
        const dataAux = params ? useCase.execute(params) : useCase.execute();
        data = await firstValueFrom(dataAux, { defaultValue: [] });
      }
      result = data;
    } catch (e: any) {
      let message =
        'master_plans.common_messages.errors.something_went_wrong_try_again';
      if (e?.error) {
        message = e.error.message ? e.error.message : e.error.errors[0];
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.messages.errors.error'),
        detail: this.translate.instant(message),
        life: 3000,
      });
    } finally {
      this.disableLoading(loaderHandler);
    }

    return result;
  }

  generateRandomGenrericColors(): string {
    const maxVal = 0xffffff; // 16777215
    let randomNumber: any;
    randomNumber = Math.random() * maxVal;
    randomNumber = Math.floor(randomNumber);
    randomNumber = randomNumber.toString(16);
    const randColor = randomNumber.padStart(6, 0);

    return `#${randColor.toUpperCase()}`;
  }

  createInstance<T>(clazz: { new (...args: any[]): T }, ...args: any[]): T {
    return new clazz(...args);
  }

  browseButtonListener() {
    const chooseButton =
      this.elref.nativeElement.querySelector('.fileupload-choose');
    if (chooseButton) {
      chooseButton.addEventListener('click', () => {
        this.onFileUploadMouseLeave();
      });
    }
    chooseButton.addEventListener('mouseup', () => {
      setTimeout(() => {
        this.onFileUploadMouseLeave();
      }, 100);
    });
  }
  getErrorMessage() {
    const errorMessageToast =
      this.elref.nativeElement.querySelector('.p-message-error');
    if (errorMessageToast) {
      this.isFileUploadErr = true;
    } else {
      this.isFileUploadErr = false;
    }
  }
  onFileUploadMouseMove() {
    this.getErrorMessage();
    const closeButton =
      this.elref.nativeElement.querySelector('.p-message-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.isFileUploadErr = false;
      });
    }
    this.onFileUploadMouseEnter();
  }
  onFileUploadMouseEnter() {
    const uploadFilesOutside = this.elref.nativeElement.querySelector(
      '.upload-files-outside'
    );
    if (uploadFilesOutside) {
      this.renderer2.addClass(uploadFilesOutside, 'drop-zone-inactive');
    }
  }
  onFileUploadMouseLeave() {
    const uploadFilesOutside = this.elref.nativeElement.querySelector(
      '.upload-files-outside'
    );
    if (uploadFilesOutside) {
      this.renderer2.removeClass(uploadFilesOutside, 'drop-zone-inactive');
    }
  }

  getSelectedMasterPlanType() {
    this.store
      .select(NewEditMasterPlansState.SelectedMasterPlanType)
      .subscribe((value) => {
        this.selectedMasterPlanTypeId = value;
      });
  }

  @ViewChildren(Menu) allMenus!: QueryList<Menu>;
  closeAllMenusExcept(exceptMenu: Menu): void {
      if (this.allMenus) {
        this.allMenus.forEach(menu => {
          if (menu !== exceptMenu && menu.visible) {
            menu.hide();
          }
        });
      }
  }
  
}
